// app/javascript/bundles/company/document_defaults/page_library/bulk_delete_modal.tsx
import React from 'react';

import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Button } from 'components/ui/button';
import { pluralize } from 'helpers/formatters/string_formats';

import { pageKey, TemplateType, type PageRecord } from './types';

interface Props {
  pages: PageRecord[];
  saving: boolean;
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

// Same reasons PageLibraryBulk#destroy returns, so the preview matches the result.
export const deleteSkipReason = (page: PageRecord): string | null => {
  if (page.templateType !== TemplateType.Pdf) return 'Only uploaded PDF pages can be deleted';
  if (page.ownedByContext === false) return 'Owned by another company';

  return null;
};

export default function BulkDeleteModal({ pages, saving, show, onClose, onConfirm }: Props) {
  const deletable = pages.filter(page => deleteSkipReason(page) === null);
  const soloPage = pages.length === 1 ? deletable[0] : undefined;
  const title = soloPage ? soloPage.name : pluralize(deletable.length, 'page');
  const subject = soloPage ? 'this page' : 'each page';
  const close = () => {
    if (saving) return;

    onClose();
  };

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered scrollable>
        <Modal.Header>
          <Modal.Title>Delete {title}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Deleting removes {subject} from every document that uses it. This can&apos;t be undone.</p>
          <List flush>
            {pages.map((page) => {
              const reason = deleteSkipReason(page);
              return (
                <List.Item className="d-flex justify-content-between" key={pageKey(page.templateType, page.id)}>
                  <span>{page.name}</span>
                  {reason && <span className="text-muted fs-small">Will be skipped: {reason}</span>}
                </List.Item>
              );
            })}
          </List>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving} variant="ghost" onClick={close}>Cancel</Button>
          <Button disabled={saving || deletable.length === 0} variant="destructive" onClick={onConfirm}>
            {saving ? 'Deleting...' : `Delete ${pluralize(deletable.length, 'page')}`}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
